/**
 * Matching a canvas filter value against an entity.
 *
 * Both canvases matched with `.includes()` for every field, so picking `sa_customer` from the
 * subject-area DROPDOWN also returned `sa_customer_return` — the value came from a list of real
 * values, so a substring match can only ever be wrong there.
 *
 * The distinction is where the value came from, not what it is called:
 *
 *   * chosen from a dropdown of existing values -> EXACT match. The user pointed at one of them.
 *   * typed free-hand into a box               -> substring. The user is searching.
 */

/** Filter fields whose value is picked from a dropdown of real values, so must match exactly. */
export const EXACT_MATCH_FILTER_FIELDS = new Set([
  // RDV canvas
  "subject_area",
  "ensemble",
  // Data mart canvas
  "datamart",
  "target_schema"
]);

/** Filter fields typed by hand, where a substring match is the point. */
export const isExactMatchFilterField = (field: string): boolean =>
  EXACT_MATCH_FILTER_FIELDS.has(field);

/**
 * Does `candidate` satisfy the filter?
 *
 * An empty needle matches everything — that is "no filter", not "match nothing".
 */
export const matchesCanvasFilter = (
  candidate: string | null | undefined,
  needle: string,
  field: string
): boolean => {
  const wanted = (needle ?? "").trim().toLowerCase();
  if (!wanted) {
    return true;
  }
  const value = (candidate ?? "").trim().toLowerCase();
  return isExactMatchFilterField(field) ? value === wanted : value.includes(wanted);
};
